import {
  ACESFilmicToneMapping,
  PCFShadowMap,
  PerspectiveCamera,
  Scene,
  SRGBColorSpace,
  WebGLRenderer,
} from 'three';
import { Assets } from './Assets.js';
import { AudioManager } from './Audio.js';
import { Input } from './Input.js';
import { Loop } from './Loop.js';
import { CameraRig } from '../camera/CameraRig.js';
import { Vehicle } from '../entities/Vehicle.js';
import { Physics } from '../physics/Physics.js';
import { HUD } from '../ui/HUD.js';
import { TouchControls } from '../ui/TouchControls.js';
import { WheelControls } from '../ui/WheelControls.js';
import { Environment } from '../world/Environment.js';
import { Level } from '../world/Level.js';
import { World } from '../world/World.js';
import { disposeObject3D } from '../utils/dispose.js';
import { clamp } from '../utils/math.js';

/**
 * Composition root: owns every subsystem, fixes update ordering and exposes a
 * very small lifecycle API to the UI (ready -> playing <-> paused).
 */
export class Game {
  constructor({ canvas, level, config, settings = null }) {
    this.canvas = canvas;
    this.levelData = level;
    this.settings = settings;
    this.config = config;

    this.state = 'boot';
    this.onStateChange = null;

    this.fps = 0;
    this._fpsTimer = 0;
    this._fpsFrames = 0;
    this._lowFuelWarned = false;
    this._refuelPrompt = '';
    this._promptKind = 'fuel';
    this._teaHeld = false;
    this._petrolHeld = false;
    this._juiceHeld = false;
    this._disposed = false;
    this.gameOverReason = null;
    this.deathCause = null;
    this._lastDamage = null;
    this.money = this.config.money?.start ?? 100;
    this._devalTimer = this.#rollDevalInterval();
    this._blackoutWasActive = false;
    this._crowFade = 0;
    this._crowNearest = Infinity;
    this._crowdSound = null;
    this._overSound = null;
  }

  async init(onProgress) {
    const report = (progress, label) => onProgress?.(progress, label);
    // Give the boot screen a chance to paint between the expensive setup
    // stages. Without this, a slow GPU (or a software-rendered WebView) can
    // leave the player staring at the initial "Preparing the world…" frame
    // until the first render finally completes.
    const yieldToPaint = () =>
      new Promise((resolve) => {
        requestAnimationFrame(() => setTimeout(resolve, 0));
      });

    report(0.04, 'Starting renderer…');
    await yieldToPaint();

    this.renderer = new WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = this.config.render.exposure;
    this.renderer.shadowMap.enabled = this.config.render.shadows;
    this.renderer.shadowMap.type = PCFShadowMap;

    this.scene = new Scene();
    this.camera = new PerspectiveCamera(
      this.config.camera.fov,
      1,
      this.config.camera.near,
      this.config.camera.far,
    );
    this.scene.add(this.camera);

    this.hud = new HUD({ config: this.config });
    this.assets = new Assets();
    this.audio = new AudioManager({
      masterVolume: this.config.audio.masterVolume,
      engine: this.config.audio.engine,
    });
    this.audio.attachTo(this.camera);

    this.#onResize();

    report(0.12, 'Loading Ammo.js physics…');
    await yieldToPaint();
    this.physics = new Physics({ gravity: this.config.physics.gravity });
    await this.physics.init();

    report(0.5, 'Building environment…');
    await yieldToPaint();
    this.input = new Input(this.canvas, { bindings: this.config.input.bindings });
    this.touchControls = new TouchControls({ input: this.input });
    this.wheelControls = new WheelControls({ input: this.input });

    // Auto-drive swaps the pedal layout for the wheel and shifter. The setting
    // is read once here and again on every change, so it can be flipped from
    // the pause menu mid-run.
    this.wheelControls.setEnabled(this.settings?.get('autoDrive') === true);
    this._unsubscribeSettings = this.settings?.onChange((id, value) => {
      if (id === 'autoDrive') this.wheelControls.setEnabled(value === true);
    });
    this.world = new World({ scene: this.scene, physics: this.physics });
    this.environment = new Environment({
      scene: this.scene,
      renderer: this.renderer,
      config: this.config,
    });

    report(0.62, 'Laying out Street 60…');
    await yieldToPaint();
    this.level = new Level({
      world: this.world,
      data: this.levelData,
      renderer: this.renderer,
      colors: this.config.colors,
      config: this.config,
    });
    // Let the level's night-aware props (lamps, windows) read the live cycle.
    this.level.environment = this.environment;
    if (this.level.traffic) {
      this.level.traffic.onQueueRage = (event) => {
        if (this.state !== 'playing') return;
        this.hud.toast(
          `Queue rage at ${event.stationName} — you skipped the line.`,
          3.8,
        );
        this.audio.horn();
      };
      this.level.traffic.onQueueHonk = () => {
        if (this.state !== 'playing') return;
        this.audio.horn();
      };
    }
    if (this.level.police) {
      this.level.police.onWarning = ({ checkpointName }) => {
        if (this.state !== 'playing') return;
        this.hud.toast(
          `DShK gunner at ${checkpointName} is tracking you — brake and reverse!`,
          3.2,
        );
      };
      this.level.police.onShot = ({ distance = 0 } = {}) => {
        this.audio.gunshot({
          volume: Math.max(0.12, 0.32 - distance * 0.004),
        });
      };
    }
    if (this.level.hitchhikers) {
      this.level.hitchhikers.onPickup = () => {
        if (this.state !== 'playing') return;
        const distance = this.config.hitchhikers?.minRideDistance ?? 100;
        this.hud.toast(
          `Hitchhiker aboard — drop them off after ${distance} m.`,
          3,
        );
      };
      this.level.hitchhikers.onDropoff = ({ count = 1, money = 0 } = {}) => {
        if (this.state !== 'playing') return;
        this.money += money;
        const noun = count === 1 ? 'Hitchhiker' : `${count} hitchhikers`;
        this.hud.toast(`${noun} dropped off · +${money} SDG`, 2.8);
      };
    }
    if (this.level.droneAttack) {
      this.level.droneAttack.onAttackStart = () => {
        if (this.state !== 'playing') return;
        this.level.traffic?.setPanic(true);
        this.hud.toast(
          'RSF drone overhead — rockets incoming! Keep moving.',
          3.2,
        );
        this.audio.droneBuzz({ volume: 0.14 });
      };
      this.level.droneAttack.onRocketLaunch = () => {
        if (this.state !== 'playing') return;
        this.audio.rocketLaunch({ volume: 0.22 });
      };
      this.level.droneAttack.onImpact = ({ distance = 0 } = {}) => {
        const closeness = Math.min(1, Math.max(0, 1 - distance / 55));
        this.audio.explosion({ volume: 0.2 + closeness * 0.44 });
        this.cameraRig?.addTrauma(0.14 + closeness * 0.56);
      };
      this.level.droneAttack.onAttackEnd = () => {
        this.level.traffic?.setPanic(false);
      };
    }

    this.cameraRig = new CameraRig({
      camera: this.camera,
      input: this.input,
      physics: this.physics,
      config: this.config.camera,
    });
    this.cameraRig.yaw = this.config.camera.initialYaw + this.level.spawnYaw;

    report(0.8, 'Rolling out the Hilux…');
    await yieldToPaint();
    this.vehicle = new Vehicle({
      physics: this.physics,
      input: this.input,
      config: this.config.vehicle,
      palette: this.config.colors,
      scene: this.scene,
      spawn: this.level.spawn.toArray(),
      spawnYaw: this.level.spawnYaw,
    });
    this.world.add(this.vehicle);
    this.vehicle.onRespawn = () => {
      this.level.droneAttack?.reset();
      this.level.traffic?.setPanic(false);
      this.level.hitchhikers?.reset(this.vehicle);
      if (this.state === 'playing') this.hud.toast('Back on Bashir Elnifidi Street.');
    };
    this.vehicle.onFuelEmpty = () => this.#gameOver('fuel');
    this.vehicle.onHealthEmpty = () =>
      this.#gameOver('health', this.#deathCause());
    this.vehicle.onDamaged = (event) => {
      // The fatal hit is the one that empties health; remember it so the
      // game-over screen can name the killer.
      this._lastDamage = event;
      if (event.source === 'drone-rocket') {
        this.hud.toast(`Rocket hit — ${event.damage} health lost.`, 2.2);
        return;
      }
      if (event.source !== 'dshk') return;
      const where = event.checkpointName ? ` at ${event.checkpointName}` : '';
      this.hud.toast(
        `DShK${where} hit — ${event.damage} health lost.`,
        1.8,
      );
    };

    report(0.9, 'Settling the physics world…');
    await yieldToPaint();
    this.physics.settle(20, this.config.physics.fixedStep);
    this.vehicle.snapVisualState();
    this.cameraRig.snapTo(this.vehicle.focus.position);
    this.environment.setFocus(this.vehicle.focus.position);

    this.loop = new Loop({
      update: this.#fixedUpdate,
      render: this.#render,
      fixedStep: this.config.physics.fixedStep,
      maxSubSteps: this.config.physics.maxSubSteps,
      maxFps: this.config.render.maxFps ?? 0,
    });

    this.input.onPointerLockChange = (locked) => {
      if (!locked && this.state === 'playing') this.pause();
    };
    this.input.onPointerLockError = () => {
      this.hud.toast('Mouse lock unavailable — right-drag to look around.', 3.5);
    };

    window.addEventListener('resize', this.#onResize);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);

    // Queue ambience: a recorded crowd (crowd.mp3) replaces the old synthesized
    // caws. Loaded once here so the bed is ready before the player reaches a
    // station. A fetch/decode failure must not block boot, so swallow it.
    try {
      await this.audio.load('crowd', '/assets/audio/crowd.mp3');
    } catch (error) {
      console.warn('[Game] Could not load the crowd ambience:', error);
    }

    // Game-over sting (over.mp3). Loaded once here so the screen never waits on
    // a fetch; a failure must not block boot.
    try {
      await this.audio.load('over', '/assets/audio/over.mp3');
    } catch (error) {
      console.warn('[Game] Could not load the game-over sting:', error);
    }

    // Compile the scene's shader programs before the first visible render.
    // Three.js can do this in parallel (KHR_parallel_shader_compile) and gives
    // the browser a chance to repaint the progress label while it works; a
    // plain render() here can otherwise block for many seconds on mobile GPUs.
    report(0.94, 'Warming up the engine…');
    await yieldToPaint();
    try {
      await this.renderer.compileAsync(this.scene, this.camera);
    } catch (error) {
      console.warn('[Game] Could not precompile shaders; rendering normally.', error);
    }

    // Render a frame before revealing the menu so the scene is visible behind it.
    await yieldToPaint();
    this.renderer.render(this.scene, this.camera);
    report(1, 'Ready');
    this.setState('ready');

    return this;
  }

  startLoop() {
    this.loop?.start();
  }

  start() {
    if (this.state !== 'ready') return;
    this.audio.unlock();
    this.input.clearTransient();
    this.setState('playing');
    // Mouse look is claimed by clicking the view, not on start: the on-screen
    // controls need a live cursor to be usable.

    if (!this._startedOnce) {
      this._startedOnce = true;
      this.hud.toast(
        'Street 60 — follow the median gaps to U-turn. Watch the fuel gauge; pull into either station and hold refuel.',
        5,
      );
    }
  }

  pause() {
    if (this.state !== 'playing') return;
    this.#stopCrowd();
    this.setState('paused');
    this.input.exitPointerLock();
    this.input.clearTransient();
  }

  resume() {
    if (this.state !== 'paused') return;
    this.audio.unlock();
    this.input.clearTransient();
    this.setState('playing');
  }

  /** Fresh run after running dry: refill, return to spawn and resume. */
  restart() {
    if (this.state !== 'gameover') return;

    this.#stopOver();
    this.audio.unlock();
    this.input.clearTransient();
    this.vehicle.repair();
    this.vehicle.refill(1);
    this.vehicle.respawn();
    this._lowFuelWarned = false;
    this._refuelPrompt = '';
    this._promptKind = 'fuel';
    this._teaHeld = false;
    this._petrolHeld = false;
    this._juiceHeld = false;
    this.gameOverReason = null;
    this.deathCause = null;
    this._lastDamage = null;
    this.money = this.config.money?.start ?? 100;
    this._devalTimer = this.#rollDevalInterval();
    this.cameraRig.snapTo(this.vehicle.mesh.position);

    this.setState('playing');
    this.hud.toast('Fresh start — full health and a full tank.', 4);
  }

  setState(state) {
    if (this.state === state) return;
    // Leaving play drops any button hold, so a pause cannot leave the throttle
    // latched, and fades the engine note out even if rAF stops.
    if (state !== 'playing') {
      this.touchControls?.releaseAll();
      this.audio?.stopEngine();
    }
    this.state = state;
    this.onStateChange?.(state);
  }

  /**
   * The forecourt is deliberately broad: the AI service lanes occupy the pump
   * islands, so the player can pull into any open part of the canopy area and
   * refuel without needing a free scripted service slot.
   */
  #findRefuelStation() {
    const position = this.vehicle.mesh.position;

    for (const station of this.level.stations ?? []) {
      const { frame, refuelZone: zone } = station;
      if (!frame || !zone) continue;

      const dx = position.x - frame.position.x;
      const dz = position.z - frame.position.z;
      const localX = dx * frame.right.x + dz * frame.right.z;
      const localZ = -(dx * frame.tangent.x + dz * frame.tangent.z);

      if (
        localX >= zone.minX &&
        localX <= zone.maxX &&
        localZ >= zone.minZ &&
        localZ <= zone.maxZ
      ) {
        return station;
      }
    }

    return null;
  }

  #updateRefuel(dt) {
    if (this.state !== 'playing') return false;

    const station = this.#findRefuelStation();
    if (!station) return false;

    const fuelConfig = this.config.vehicle.fuel;
    // Round-off from a full tank (and the first tiny suspension-induced creep)
    // should still count as full; otherwise the prompt flickers straight back
    // to "Hold to refuel". The threshold is close enough to 1 that one more top-up
    // step can still reach an exact fill and fire the "Tank full" toast.
    if (this.vehicle.fuel >= 0.999) return false;

    if (Math.abs(this.vehicle.speed) > fuelConfig.refuelSpeedLimit) {
      this._refuelPrompt = `Stop to refuel · ${station.nameEnglish}`;
      return true;
    }

    if (!this.input.isDown('refuel')) {
      this._refuelPrompt = `Hold to refuel · ${station.nameEnglish}`;
      return true;
    }

    const before = this.vehicle.fuel;
    this.vehicle.addFuel(fuelConfig.refuelRate * dt);
    this._refuelPrompt = `Refueling · ${station.nameEnglish}`;

    if (before < 1 && this.vehicle.fuel >= 1) {
      this.hud.toast('Tank full — ready to roll.', 2.4);
    }

    return true;
  }

  /** The nearest tea stand whose interaction radius covers the truck. */
  #findTeaStand() {
    const position = this.vehicle.mesh.position;
    const radius = this.config.tea.radius;

    for (const stand of this.level.teaStands?.stands ?? []) {
      const dx = position.x - stand.position.x;
      const dz = position.z - stand.position.z;
      if (dx * dx + dz * dz <= radius * radius) return stand;
    }

    return null;
  }

  /**
   * A jabana from a tea lady. Costs money (SDG), gives health back. The
   * purchase is edge-triggered on the refuel control so holding it down to
   * refuel at a station and then coasting to a stand cannot buy by accident.
   */
  #updateTea() {
    if (this.state !== 'playing') return false;

    const stand = this.#findTeaStand();
    if (!stand) {
      this._teaHeld = false;
      return false;
    }

    const tea = this.config.tea;
    const { health, maxHealth } = this.vehicle;

    const held = this.input.isDown('refuel');
    const pressed = held && !this._teaHeld;
    this._teaHeld = held;

    if (health >= maxHealth) {
      this._refuelPrompt = `Health full · jabana ${tea.price} SDG`;
      return true;
    }

    if (Math.abs(this.vehicle.speed) > tea.speedLimit) {
      this._refuelPrompt = `Stop for jabana · ${tea.price} SDG`;
      return true;
    }

    if (this.money < tea.price) {
      this._refuelPrompt = `Jabana costs ${tea.price} SDG`;
      return true;
    }

    this._refuelPrompt = `Buy jabana · +${tea.heal} health for ${tea.price} SDG`;

    if (pressed) {
      this.money -= tea.price;
      const healed = this.vehicle.heal(tea.heal);
      stand.purchases += 1;
      this.hud.toast(
        `Jabana — +${healed} health. ${tea.price} SDG spent.`,
        2.6,
      );
      this.#checkBankrupt();
    }

    return true;
  }

  /** The ٥٠ فاكهة juice shop, if the truck is parked in front of it. */
  #findJuiceShop() {
    const shop = this.level.juiceShop;
    if (!shop?.position) return null;

    const position = this.vehicle.mesh.position;
    const radius = this.config.juice.radius;
    const dx = position.x - shop.position.x;
    const dz = position.z - shop.position.z;
    return dx * dx + dz * dz <= radius * radius ? shop : null;
  }

  /**
   * A fresh juice from ٥٠ فاكهة. It follows the jabana rules deliberately:
   * one press costs a flat fee and restores a little health; holding the
   * refuel control cannot buy twice, and a stopped truck is required.
   */
  #updateJuice() {
    if (this.state !== 'playing') return false;

    const shop = this.#findJuiceShop();
    if (!shop) {
      this._juiceHeld = false;
      return false;
    }

    const juice = this.config.juice;
    const { health, maxHealth } = this.vehicle;

    const held = this.input.isDown('refuel');
    const pressed = held && !this._juiceHeld;
    this._juiceHeld = held;

    if (health >= maxHealth) {
      this._refuelPrompt = `Health full · juice ${juice.price} SDG`;
      return true;
    }

    if (Math.abs(this.vehicle.speed) > juice.speedLimit) {
      this._refuelPrompt = `Stop for juice · ${juice.price} SDG`;
      return true;
    }

    if (this.money < juice.price) {
      this._refuelPrompt = `Juice costs ${juice.price} SDG`;
      return true;
    }

    this._refuelPrompt = `Buy juice · +${juice.heal} health for ${juice.price} SDG`;

    if (pressed) {
      this.money -= juice.price;
      const healed = this.vehicle.heal(juice.heal);
      shop.purchases += 1;
      this.hud.toast(
        `Fresh juice — +${healed} health. ${juice.price} SDG spent.`,
        2.6,
      );
      this.#checkBankrupt();
    }

    return true;
  }

  /** The nearest black-market petrol stall whose radius covers the truck. */
  #findPetrolStand() {
    const position = this.vehicle.mesh.position;
    const radius = this.config.petrol.radius;

    for (const stand of this.level.petrolStands?.stands ?? []) {
      const dx = position.x - stand.position.x;
      const dz = position.z - stand.position.z;
      if (dx * dx + dz * dz <= radius * radius) return stand;
    }

    return null;
  }

  /**
   * A tank of petrol off the back of a stall. Unlike the station there is no
   * pump and no lever: one press empties the wallet of a flat fee and fills the
   * tank outright. Edge-triggered on the same control the pump uses, so holding
   * the lever while coasting past him cannot buy by accident.
   */
  #updatePetrol() {
    if (this.state !== 'playing') return false;

    const stand = this.#findPetrolStand();
    if (!stand) {
      this._petrolHeld = false;
      return false;
    }

    const petrol = this.config.petrol;

    const held = this.input.isDown('refuel');
    const pressed = held && !this._petrolHeld;
    this._petrolHeld = held;

    if (this.vehicle.fuel >= 0.999) {
      this._refuelPrompt = `Tank full · petrol ${petrol.price} SDG`;
      return true;
    }

    if (Math.abs(this.vehicle.speed) > petrol.speedLimit) {
      this._refuelPrompt = `Stop for petrol · ${petrol.price} SDG`;
      return true;
    }

    if (this.money < petrol.price) {
      this._refuelPrompt = `Petrol costs ${petrol.price} SDG`;
      return true;
    }

    this._refuelPrompt = `Fill up from the cans · ${petrol.price} SDG`;

    if (pressed) {
      this.money -= petrol.price;
      this.vehicle.refill(1);
      stand.purchases += 1;
      this.hud.toast(`Tank filled from the jerry cans · ${petrol.price} SDG.`, 2.8);
      this.#checkBankrupt();
    }

    return true;
  }

  /** Contextual action for whatever the truck is parked beside. */
  #updateActions(dt) {
    this._refuelPrompt = '';
    this._promptKind = 'fuel';
    if (this.state !== 'playing') return;

    if (this.#updateRefuel(dt)) return;

    this._promptKind = 'tea';
    if (this.#updateTea()) return;

    this._promptKind = 'petrol';
    if (this.#updatePetrol()) return;

    this._promptKind = 'juice';
    if (this.#updateJuice()) return;

    this._promptKind = 'fuel';
  }

  #updateFuelWarning() {
    const threshold = this.config.vehicle.fuel.lowThreshold;

    if (this.vehicle.fuel > threshold + 0.02) {
      this._lowFuelWarned = false;
    }
    if (
      this.vehicle.fuel > threshold ||
      this._lowFuelWarned ||
      this.vehicle.fuel <= 0
    ) {
      return;
    }

    this._lowFuelWarned = true;
    this.hud.toast('Fuel low — pull into Bashair or Nabta and hold refuel.', 5);
  }

  /**
   * Sudanese crows calling from the forecourt trees along the fuel queue.
   *
   * The birds get louder as the truck comes up on "the gas line", so the
   * station announces itself before the canopy comes into view. Calls come in
   * small clusters — two to four caws — spaced at random, and the whole thing
   * fades out once the queue is behind the truck.
   */
  #updateGasLineCrows(frameDt) {
    const settings = this.config.crows;
    const zones = this.level.gasLineZones;
    if (!settings?.enabled || !zones?.length) return;

    // Nearest point on any gas line.
    const position = this.vehicle.focus.position;
    let nearest = Infinity;
    let nearestZone = null;
    for (const zone of zones) {
      for (const sample of zone.samples) {
        const distance = sample.distanceToSquared(position);
        if (distance < nearest) {
          nearest = distance;
          nearestZone = zone;
        }
      }
    }
    this._crowNearest = Math.sqrt(nearest);

    // Fade the crows in and out around the trigger radius.
    const trigger = settings.triggerRadius;
    const inner = Math.max(1, settings.innerRadius);
    let target = 0;
    if (this._crowNearest <= inner) {
      target = 1;
    } else if (this._crowNearest < trigger) {
      target = 1 - (this._crowNearest - inner) / (trigger - inner);
    }

    const rate = target > this._crowFade ? 1 / Math.max(0.05, settings.fadeIn) : 1 / Math.max(0.05, settings.fadeOut);
    this._crowFade = clamp(this._crowFade + (target - this._crowFade) * Math.min(1, rate * frameDt), 0, 1);

    if (this._crowFade <= 0.01 || !nearestZone) {
      // Out of range: drop the crowd loop if it is still running.
      this.#stopCrowd();
      return;
    }

    // Inside range: keep a single looped crowd bed and ride its level with the
    // proximity fade. The recording replaces the old synthesized caws.
    if (!this._crowdSound) {
      this._crowdSound = this.audio.playCrowd({ volume: 0 });
    }
    if (this._crowdSound) {
      // Set the gain value directly rather than Audio.setVolume(): that method
      // schedules a setTargetAtTime ramp whose clock is frozen in headless
      // runs, so a plain assignment is both testable and reliable in-browser.
      this._crowdSound.gain.gain.value = settings.maxVolume * this._crowFade;
    }
  }

  /** Stop and release the looped crowd bed if it is currently playing. */
  #stopCrowd() {
    if (this._crowdSound) {
      try {
        this._crowdSound.stop();
      } catch {
        /* already stopped or the context is closed */
      }
      this._crowdSound = null;
    }
  }

  /** Stop the one-shot game-over sting (e.g. when restarting a run). */
  #stopOver() {
    if (!this._overSound) return;
    try {
      this._overSound.stop();
    } catch {
      /* already stopped or the context is closed */
    }
    this._overSound = null;
  }

  /** Map the fatal damage event to one of the death-screen causes. */
  #deathCause() {
    const event = this._lastDamage;
    if (!event) return 'other';
    switch (event.source) {
      case 'drone-rocket':
        return 'drone';
      case 'dshk':
        return 'dshk';
      case 'rage':
        return 'rage';
      case 'impact':
        return event.car?.type === 'military' ? 'militia' : 'reckless';
      default:
        return 'other';
    }
  }

  #gameOver = (reason = 'fuel', cause = null) => {
    if (this.state !== 'playing') return;

    this.#stopCrowd();
    this.#stopOver();
    this._overSound = this.audio.play('over', { volume: 0.9 });

    this.gameOverReason = reason;
    this.deathCause = cause;
    this.level.droneAttack?.reset();
    this.level.traffic?.setPanic(false);
    this._refuelPrompt = '';
    this._promptKind = 'fuel';
    this._teaHeld = false;
    this._petrolHeld = false;
    this._juiceHeld = false;
    this.setState('gameover');
    this.input.exitPointerLock();
    this.input.clearTransient();
  };

  /** Random gap, in seconds, until the next SDG slide. */
  #rollDevalInterval() {
    const settings = this.config.economy?.devaluation;
    if (!settings) return Infinity;
    const min = settings.intervalMin ?? 20;
    const max = Math.max(min, settings.intervalMax ?? 45);
    return min + Math.random() * (max - min);
  }

  /**
   * The dollar goes up against the pound: a random slice of the player's SDG is
   * wiped out, so the money number on the HUD simply drops. A dedicated
   * on-screen alert announces the jump and the amount lost; no exchange-rate
   * figure is shown.
   */
  #applyDevaluation() {
    const settings = this.config.economy?.devaluation;
    if (!settings?.enabled) return;

    const balance = Math.floor(this.money);
    if (balance <= 0) return;

    const pctMin = settings.percentMin ?? 0.05;
    const pctMax = Math.max(pctMin, settings.percentMax ?? 0.15);
    const pct = pctMin + Math.random() * (pctMax - pctMin);

    let loss = Math.round(balance * pct);
    loss = Math.max(settings.minLoss ?? 4, Math.min(settings.maxLoss ?? 150, loss));
    loss = Math.min(loss, balance);
    if (loss <= 0) return;

    this.money = Math.max(0, this.money - loss);
    this.hud.alert({
      title: 'The dollar has gone up',
      amount: `−${loss} SDG`,
    });
    this.#checkBankrupt();
  }

  /**
   * Broke on Street 60. The HUD floors the balance, so once it can no longer
   * show a whole pound the run is over.
   */
  #checkBankrupt() {
    if (this.state !== 'playing') return;
    if (this.money >= 1) return;
    this.#gameOver('money');
  }

  /** Count down to the next devaluation while the player is on the road. */
  #updateCurrency(dt) {
    const settings = this.config.economy?.devaluation;
    if (!settings?.enabled) return;
    this._devalTimer -= dt;
    if (this._devalTimer > 0) return;
    this._devalTimer = this.#rollDevalInterval();
    this.#applyDevaluation();
  }

  #fixedUpdate = (dt) => {
    if (this.state !== 'playing') return;

    if (this.input.consumePressed('pause')) {
      this.pause();
      return;
    }

    // Broke is a lose condition: the run ends the moment the wallet hits zero.
    if (this.money < 1) {
      this.#gameOver('money');
      return;
    }

    this.money += (this.config.money?.pointsPerSecond ?? 0.5) * dt;
    this.#updateCurrency(dt);
    this.world.fixedUpdate(dt);
    this.physics.step(dt);
    this.physics.syncMeshes();
    this.world.lateUpdate(dt);

    if (this.state !== 'playing') return;
    this.#updateActions(dt);
    if (this.state !== 'playing') return;
    this.#updateFuelWarning();
  };

  #render = (frameDt, alpha) => {
    // When rendering is capped to the physics tick, show the newest pose:
    // interpolation would only add a tick of visual/input latency. The blend
    // is still available for uncapped rendering on high-refresh displays.
    const interpolate = this.config.render.interpolate && this.state === 'playing';
    this.vehicle.render(interpolate ? alpha : 1, frameDt);

    // Speed and throttle drive the synthesized engine note. The manager
    // rate-limits its parameter updates, so calling it every rendered frame is
    // cheap even at a high refresh rate.
    this.audio.updateEngine(
      {
        active: this.state === 'playing',
        throttle: this.input.throttleAxis,
        reversing: this.vehicle.reversing,
        speed: this.vehicle.speed,
        maxSpeed: this.config.vehicle.maxSpeed,
      },
      frameDt,
    );

    // A slow orbit keeps the scene alive behind the start menu.
    if (this.state === 'ready') this.cameraRig.yaw += frameDt * 0.035;

    this.cameraRig.update(frameDt, this.vehicle.focus);
    this.environment.setFocus(this.vehicle.focus.position);
    const viewDistance = this.camera.position.distanceTo(this.vehicle.focus.position);
    this.environment.update(frameDt, viewDistance);

    // Announce a fresh blackout on the same banner the dollar jumps use. If it
    // begins while paused, hold the announcement until the player is back in.
    const blackoutActive = this.environment.blackout;
    if (blackoutActive && !this._blackoutWasActive) {
      if (this.state === 'playing') {
        this.hud.alert({
          title: 'The power has gone out',
          amount: `${Math.round(this.config.blackout?.duration ?? 10)} SEC`,
        });
        this._blackoutWasActive = true;
      }
    } else if (!blackoutActive) {
      this._blackoutWasActive = false;
    }

    // Night-aware world props follow the cycle every frame (menu and play alike).
    // A power blackout (night only) kills the city's lights but leaves the
    // player's own headlights burning, so `lit` drives the grid-owned glows.
    const night = this.environment.nightFactor;
    const blackout = this.environment.blackoutFactor;
    const lit = night * (1 - blackout);
    if (this.level?.city?.lamps) {
      this.level.city.lamps.update(this.vehicle.focus.position, lit);
    }
    if (this.level?.city?.windows) {
      this.level.city.windows.material.emissiveIntensity = lit * 1.5;
    }
    this.level?.juiceShop?.update(lit);
    this.vehicle.setHeadlightLevel(night, blackout);

    if (this.state === 'playing') this.level.update(frameDt, this.vehicle);
    if (this.state === 'playing') this.#updateGasLineCrows(frameDt);

    this._fpsFrames += 1;
    this._fpsTimer += frameDt;
    if (this._fpsTimer >= 0.5) {
      this.fps = Math.round(this._fpsFrames / this._fpsTimer);
      this._fpsFrames = 0;
      this._fpsTimer = 0;
    }

    this.hud.update(frameDt, {
      fps: this.fps,
      bodies: this.physics.bodyCount,
      money: this.money,
      health: this.vehicle.health,
      maxHealth: this.vehicle.maxHealth,
      fuel: this.vehicle.fuel,
      refuelPrompt: this.state === 'playing' ? this._refuelPrompt : '',
      promptKind: this._promptKind,
      rideStatus:
        this.state === 'playing' ? (this.level.hitchhikers?.status ?? '') : '',
      rideMode: this.level.hitchhikers?.statusMode ?? 'hail',
    });

    this.renderer.render(this.scene, this.camera);
  };

  #onResize = () => {
    if (!this.renderer || !this.camera) return;

    const width = window.innerWidth;
    const height = window.innerHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();

    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.config.render.maxPixelRatio));
    this.renderer.setSize(width, height, false);
  };

  #onVisibilityChange = () => {
    if (document.hidden && this.state === 'playing') this.pause();
  };

  dispose() {
    if (this._disposed) return;
    this._disposed = true;

    this.#stopCrowd();
    this.#stopOver();
    this.loop?.stop();
    window.removeEventListener('resize', this.#onResize);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);

    this.vehicle?.dispose();
    this.level?.dispose();
    this.world?.clear();
    if (this.vehicle) disposeObject3D(this.vehicle.mesh);

    this.environment?.dispose();
    this.physics?.dispose();
    this.audio?.dispose();
    this.touchControls?.dispose();
    this.wheelControls?.dispose();
    this._unsubscribeSettings?.();
    this.input?.dispose();
    this.assets?.dispose();
    this.hud?.dispose();

    this.scene?.clear();
    this.renderer?.dispose();
  }
}
